import type { MouseEvent } from 'react';
import React, { useRef } from 'react';
import {
  IconButton,
  IconClose,
  IconError,
  LoadingSpinner,
  SolidButton,
} from '@noths/polaris-client-ribbons-design-system';

import type { InitialAttachment } from 'src/components/molecules/AttachmentUploader/hooks/useAttachmentUpload';
import { useAttachmentUpload } from 'src/components/molecules/AttachmentUploader/hooks/useAttachmentUpload';
import * as styles from 'src/components/molecules/AttachmentUploader/styles/AttachmentUploader.styles';
import {
  ATTACHMENT_UPLOADER_ERROR_MESSAGE_TESTID,
  ATTACHMENT_UPLOADER_EXTERNAL_ERROR_MESSAGE_TESTID,
  ATTACHMENT_UPLOADER_TESTID,
} from 'src/components/organisms/Signup/constants/testIds';

const DEFAULT_MAX_FILE_SIZE = 104857600;
export const DEFAULT_UPLOAD_LABEL = 'Upload';

export interface AttachmentUploaderProps {
  error?: string;
  initialAttachments?: InitialAttachment[];
  label?: string;
  maxFileSize?: number;
  maxUploads?: number;
  mimeTypes?: string[];
  onAttachmentRemoved?: (attachementId: string) => void;
  onUploadButtonClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onUploadCompleted?: (attachementId: string, fileName: string, uploadReference: string) => void;
  onUploadError?: (attachementId: string, fileName: string) => void;
  onUploadStarted?: (attachementId: string, fileName: string) => void;
  required?: boolean;
  shouldUploadAttachment?: (fileName: string) => string | null;
}

export const AttachmentUploader = ({
  error,
  initialAttachments = [],
  label = DEFAULT_UPLOAD_LABEL,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxUploads = 0,
  mimeTypes = [],
  onAttachmentRemoved = (_attachmentId) => {},
  onUploadButtonClick = (_event) => null,
  onUploadCompleted = (_attachmentId, _fileName, _uploadReference) => {},
  onUploadError = (_attachmentId, _fileName) => {},
  onUploadStarted = (_attachmentId, _fileName) => {},
  shouldUploadAttachment = (_fileName) => null,
}: AttachmentUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onClickBeginFileInputDialog = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    fileInputRef.current?.click();
    onUploadButtonClick(e);
  };
  const acceptedMimeTypes = mimeTypes.join(',');

  const { attachments, deleteUploadedFile, handleFileChange, isUploadDisabled, uploadError } =
    useAttachmentUpload(
      maxUploads,
      mimeTypes,
      maxFileSize,
      initialAttachments,
      shouldUploadAttachment,
      onUploadStarted,
      onUploadError,
      onUploadCompleted,
      onAttachmentRemoved,
    );

  return (
    <>
      <div data-testid={ATTACHMENT_UPLOADER_TESTID}>
        {attachments.map(({ id, name, uploaded }) => (
          <div key={name}>
            <div
              css={styles.fileUploadedContainer}
              data-testid={`attachment-uploader-file-${name}`}
            >
              <div css={styles.uploadedFileName}>{name}</div>
              {uploaded ? (
                <div data-testid={`attachment-uploader-delete-button-${name}`}>
                  <IconButton
                    Icon={IconClose}
                    aria-label="Delete attachment"
                    css={styles.deleteUploadedFileButton}
                    onClick={() => deleteUploadedFile(id)}
                    size="small"
                  />
                </div>
              ) : (
                <div data-testid={`attachment-uploader-progress-icon-${name}`}>
                  <LoadingSpinner aria-label="Attachment uploading" css={styles.uploadingSpinner} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <SolidButton
        css={styles.uploadButton}
        data-testid="attachment-uploader-button"
        disabled={isUploadDisabled()}
        id="btn-trigger-hidden-upload-dialog"
        onClick={onClickBeginFileInputDialog}
      >
        {label}
      </SolidButton>
      <input
        accept={acceptedMimeTypes}
        aria-invalid={!!uploadError}
        data-testid="attachment-uploader-input"
        hidden
        id="hidden-upload-dialog"
        onChange={handleFileChange}
        ref={fileInputRef}
        type="file"
      />
      {uploadError && (
        <div css={styles.errorField} data-testid={ATTACHMENT_UPLOADER_ERROR_MESSAGE_TESTID}>
          <IconError height={16} width={16} />
          <span>{uploadError.message}</span>
        </div>
      )}
      {error && (
        <div
          css={styles.errorField}
          data-testid={ATTACHMENT_UPLOADER_EXTERNAL_ERROR_MESSAGE_TESTID}
        >
          <IconError height={16} width={16} />
          <span>{error}</span>
        </div>
      )}
    </>
  );
};
